import { api, getToken, type AnalyticsFeature, type AnalyticsSourceCategory } from "./api";
import { disableGoogleAnalytics } from "./googleAnalytics";
import { isNativeApp } from "./platform";
import { androidAnalyticsBlocked, LEGACY_ANALYTICS_OPT_OUT_KEY, LEGACY_NATIVE_ANALYTICS_OPT_OUT_KEY, persistAndroidAnalyticsOptOut } from "./androidRestoreSettings";

export const ANALYTICS_BROWSER_KEY = "kp_analytics_browser_key";
export const ANALYTICS_OPT_OUT_KEY = LEGACY_ANALYTICS_OPT_OUT_KEY;
const ANALYTICS_OBJECTION_PENDING_KEY = "kp_analytics_objection_pending";

const ANALYTICS_BROWSER_KEY_EXPIRES_AT = `${ANALYTICS_BROWSER_KEY}_expires_at`;
const ANALYTICS_VISIT_KEY = "kp_analytics_visit";
const NATIVE_ANALYTICS_BROWSER_KEY = `${ANALYTICS_BROWSER_KEY}_native`;
const NATIVE_ANALYTICS_BROWSER_KEY_EXPIRES_AT = `${NATIVE_ANALYTICS_BROWSER_KEY}_expires_at`;
const NATIVE_ANALYTICS_VISIT_KEY = `${ANALYTICS_VISIT_KEY}_native`;
const NATIVE_ANALYTICS_OPT_OUT_KEY = LEGACY_NATIVE_ANALYTICS_OPT_OUT_KEY;
const NATIVE_ANALYTICS_OBJECTION_PENDING_KEY = `${ANALYTICS_OBJECTION_PENDING_KEY}_native`;
const SIGNUP_ANALYTICS_CONFIG_TIMEOUT_MS = 1_000;
const DAY_MS = 24 * 60 * 60 * 1000;
const BROWSER_KEY_TTL_MS = 365 * DAY_MS;
const VISIT_TIMEOUT_MS = 30 * 60 * 1000;
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

type VisitState = {
  visitKey: string;
  lastEventAt: number | null;
  sourceCategory: AnalyticsSourceCategory;
};

const pendingVisitEvents = new Map<string, { lastAttemptAt: number; ids: Set<number> }>();
let nextVisitEventId = 0;
let documentLandingSourceUsed = false;

const SOURCE_CATEGORIES = new Set<AnalyticsSourceCategory>([
  "search", "social", "referral", "direct", "other", "unknown",
]);

const SEARCH_HOSTS = [
  "google.com", "google.co.uk", "google.co.jp", "google.co.in", "google.com.au", "google.com.br", "google.com.ph",
  "google.com.mx", "google.com.sg", "google.com.hk", "google.co.nz", "google.co.kr", "google.co.id", "google.co.za",
  "google.com.tr", "google.de", "google.fr", "google.es", "google.it", "google.nl", "bing.com", "duckduckgo.com",
  "yahoo.com", "baidu.com", "yandex.com", "ecosia.org", "brave.com",
];
const SOCIAL_HOSTS = [
  "facebook.com", "instagram.com", "linkedin.com", "x.com", "twitter.com", "tiktok.com",
  "youtube.com", "youtu.be", "reddit.com", "pinterest.com", "threads.net", "snapchat.com",
];

const SENSITIVE_PAGE_PATHS = [
  /^\/admin(?:\/|$)/i,
  /^\/p(?:\/|$)/i,
  /^\/(?:orders|engagement|messages|contracts|wallet|billing|earnings|settings|support|verify|get-verified|worker-profile|post|dashboard|saved|invite)(?:\/|$)/i,
  /^\/(?:login|signup|forgot-password|reset-password|welcome)(?:\/|$)/i,
  /^\/company\/new(?:\/|$)/i,
  /^\/company\/[^/]+\/(?:edit|licenses|dashboard|manage)(?:\/|$)/i,
];

export function browserAnalyticsOptedOut() {
  if (androidAnalyticsBlocked()) return true;
  try {
    return typeof window === "undefined" || window.localStorage.getItem(ANALYTICS_OPT_OUT_KEY) === "true";
  } catch {
    // Capacitor has a separate persistent preference store. Its async opt-out
    // check is performed by the member-event emitter before contacting config.
    return !isNativeApp();
  }
}

/** A pending objection keeps its retry action available after a page reload. */
export async function hasPendingAnalyticsObjection() {
  const storage = getBrowserStorage();
  if (storage) {
    try {
      if (storage.getItem(ANALYTICS_OBJECTION_PENDING_KEY) === "true") return true;
      if (storage.getItem(ANALYTICS_OPT_OUT_KEY) === "true" && storage.getItem(ANALYTICS_BROWSER_KEY)) return true;
    } catch { /* check the native preference store below */ }
  }

  if (!isNativeApp()) return false;
  const preferences = await getNativeAnalyticsPreferences();
  if (!preferences) return false;
  try {
    const pending = await preferences.get({ key: NATIVE_ANALYTICS_OBJECTION_PENDING_KEY });
    if (pending.value === "true") return true;
    const [optedOut, browserKey] = await Promise.all([
      preferences.get({ key: NATIVE_ANALYTICS_OPT_OUT_KEY }),
      preferences.get({ key: NATIVE_ANALYTICS_BROWSER_KEY }),
    ]);
    return optedOut.value === "true" && Boolean(browserKey.value);
  } catch {
    return false;
  }
}

/** Keep private document and account surfaces out of visitor collection. */
export function isAnalyticsPageViewPathEligible(path: string) {
  const normalizedPath = path.split("?", 1)[0] || "/";
  return !SENSITIVE_PAGE_PATHS.some((pattern) => pattern.test(normalizedPath));
}

/** Read no page query values and expose no route/referrer text to the event API. */
export function isForegroundBrowserDocument() {
  try {
    return typeof document !== "undefined" && document.visibilityState === "visible";
  } catch {
    return false;
  }
}

/** Map only explicit feature routes; route identifiers and query values are never sent. */
export function analyticsFeatureForPath(path: string): AnalyticsFeature | null {
  const normalizedPath = (path.split(/[?#]/, 1)[0] || "/").toLowerCase();
  // Directory paths redirect into Marketplace, which records the selected tab.
  if (/^\/j\/[^/]+\/?$/.test(normalizedPath)) return "jobs";
  if (/^\/project\/[^/]+\/?$/.test(normalizedPath)) return "projects";
  if (/^\/rfq\/[^/]+\/?$/.test(normalizedPath)) return "rfqs";
  if (/^\/s\/[^/]+\/?$/.test(normalizedPath)) return "services";
  if (/^\/item\/[^/]+\/?$/.test(normalizedPath)) return "items";
  if (/^\/messages(?:\/[^/]+)?\/?$/.test(normalizedPath)) return "messaging";
  if (/^\/contracts\/?$/.test(normalizedPath)) return "contracts";
  return null;
}

/** Map only the explicitly allowlisted feature tabs in the combined marketplace. */
export function analyticsFeatureForMarketplaceTab(tab: string): AnalyticsFeature | null {
  switch (tab) {
    case "Jobs": return "jobs";
    case "Project Bidding": return "projects";
    case "RFQs": return "rfqs";
    case "Services": return "services";
    case "Marketplace": return "items";
    default: return null;
  }
}

function getBrowserStorage(): Storage | null {
  try {
    return typeof window === "undefined" ? null : window.localStorage;
  } catch {
    return null;
  }
}

function readExistingBrowserKey(storage: Storage, now: number): string | null {
  const browserKey = storage.getItem(ANALYTICS_BROWSER_KEY);
  const expiresAt = Number(storage.getItem(ANALYTICS_BROWSER_KEY_EXPIRES_AT));
  return browserKey && UUID_PATTERN.test(browserKey) && Number.isFinite(expiresAt) && expiresAt > now
    ? browserKey
    : null;
}

function createRandomUuid(): string | null {
  try {
    if (typeof crypto === "undefined") return null;
    if (typeof crypto.randomUUID === "function") return crypto.randomUUID();
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = [...bytes].map((byte) => byte.toString(16).padStart(2, "0")).join("");
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  } catch {
    return null;
  }
}

function createBrowserKey(storage: Storage, now: number): string | null {
  try {
    const existingKey = readExistingBrowserKey(storage, now);
    if (existingKey) return existingKey;

    storage.removeItem(ANALYTICS_BROWSER_KEY);
    storage.removeItem(ANALYTICS_BROWSER_KEY_EXPIRES_AT);
    storage.removeItem(ANALYTICS_VISIT_KEY);
    const browserKey = createRandomUuid();
    if (!browserKey) return null;
    storage.setItem(ANALYTICS_BROWSER_KEY, browserKey);
    storage.setItem(ANALYTICS_BROWSER_KEY_EXPIRES_AT, String(now + BROWSER_KEY_TTL_MS));
    return browserKey;
  } catch {
    try {
      storage.removeItem(ANALYTICS_BROWSER_KEY);
      storage.removeItem(ANALYTICS_BROWSER_KEY_EXPIRES_AT);
    } catch { /* unavailable storage means collection stays off */ }
    return null;
  }
}

/** Return only a still-valid key already created by an allowed page-view collection. */
export async function existingAnalyticsBrowserKeyForSignup(): Promise<string | undefined> {
  if (isNativeApp() || browserAnalyticsOptedOut()) return undefined;

  let config: Awaited<ReturnType<typeof api.analyticsConfig>>;
  let timeoutId: number | undefined;
  try {
    const controller = new AbortController();
    timeoutId = window.setTimeout(() => controller.abort(), SIGNUP_ANALYTICS_CONFIG_TIMEOUT_MS);
    config = await api.analyticsConfig(controller.signal);
  } catch {
    return undefined;
  } finally {
    if (timeoutId !== undefined) window.clearTimeout(timeoutId);
  }
  if (!config?.enabled || config.memberOptedOut || browserAnalyticsOptedOut()) return undefined;

  const storage = getBrowserStorage();
  if (!storage) return undefined;
  try {
    return readExistingBrowserKey(storage, Date.now()) ?? undefined;
  } catch {
    return undefined;
  }
}

function hostMatches(hostname: string, domain: string) {
  return hostname === domain || hostname.endsWith(`.${domain}`);
}

function normalizeHostname(hostname: string) {
  return hostname.toLowerCase().replace(/\.$/, "").replace(/^www\./, "");
}

/** Convert the document referrer to a single allowed category without retaining it. */
function landingSourceCategory(): AnalyticsSourceCategory {
  try {
    const referrer = document.referrer;
    if (!referrer) return "direct";
    const url = new URL(referrer);
    if (url.protocol !== "http:" && url.protocol !== "https:") return "other";
    const hostname = normalizeHostname(url.hostname);
    if (!hostname) return "unknown";
    if (hostname === normalizeHostname(window.location.hostname)) return "direct";

    if (SEARCH_HOSTS.some((domain) => hostMatches(hostname, domain))) {
      return "search";
    }
    if (SOCIAL_HOSTS.some((domain) => hostMatches(hostname, domain))) return "social";
    return "referral";
  } catch {
    return "unknown";
  }
}

function readVisitState(raw: string | null): VisitState | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<VisitState>;
    if (
      typeof parsed.visitKey === "string" && UUID_PATTERN.test(parsed.visitKey) &&
      (parsed.lastEventAt === null || (typeof parsed.lastEventAt === "number" && Number.isFinite(parsed.lastEventAt))) &&
      typeof parsed.sourceCategory === "string" && SOURCE_CATEGORIES.has(parsed.sourceCategory as AnalyticsSourceCategory)
    ) {
      return parsed as VisitState;
    }
  } catch { /* discard malformed local state */ }
  return null;
}

function createOrReuseVisit(storage: Storage, now: number): VisitState | null {
  try {
    const previous = readVisitState(storage.getItem(ANALYTICS_VISIT_KEY));
    const pending = previous ? pendingVisitEvents.get(previous.visitKey) : undefined;
    const recentSuccess = previous?.lastEventAt !== null && previous?.lastEventAt !== undefined &&
      now >= previous.lastEventAt && now - previous.lastEventAt < VISIT_TIMEOUT_MS;
    const recentAttempt = pending && now >= pending.lastAttemptAt && now - pending.lastAttemptAt < VISIT_TIMEOUT_MS;
    if (previous && (recentSuccess || recentAttempt)) {
      return previous;
    }

    const visitKey = createRandomUuid();
    if (!visitKey) return null;
    const sourceCategory = documentLandingSourceUsed ? "direct" : landingSourceCategory();
    return { visitKey, lastEventAt: null, sourceCategory };
  } catch {
    return null;
  }
}

function beginVisitEvent(visitKey: string, now: number) {
  const id = ++nextVisitEventId;
  const pending = pendingVisitEvents.get(visitKey) ?? { lastAttemptAt: now, ids: new Set<number>() };
  pending.lastAttemptAt = now;
  pending.ids.add(id);
  pendingVisitEvents.set(visitKey, pending);
  return id;
}

function endVisitEvent(visitKey: string, id: number) {
  const pending = pendingVisitEvents.get(visitKey);
  if (!pending) return;
  pending.ids.delete(id);
  if (pending.ids.size === 0) pendingVisitEvents.delete(visitKey);
}

function recordSuccessfulVisitEvent(storage: Storage, visitKey: string) {
  try {
    const current = readVisitState(storage.getItem(ANALYTICS_VISIT_KEY));
    if (current?.visitKey === visitKey) {
      persistVisitState(storage, { ...current, lastEventAt: Date.now() });
    }
  } catch { /* local event time is only session bookkeeping */ }
}

function persistVisitState(storage: Storage, visit: VisitState) {
  try {
    storage.setItem(ANALYTICS_VISIT_KEY, JSON.stringify(visit));
    return true;
  } catch {
    return false;
  }
}

function clearBrowserIdentity(storage: Storage) {
  try {
    storage.removeItem(ANALYTICS_BROWSER_KEY);
    storage.removeItem(ANALYTICS_BROWSER_KEY_EXPIRES_AT);
    storage.removeItem(ANALYTICS_VISIT_KEY);
  } catch { /* best effort when browser storage is restricted */ }
}

/** Send one minimal page-view event after both objection gates and config allow it. */
export async function emitAnalyticsPageView(path: string) {
  if (!isAnalyticsPageViewPathEligible(path) || !isForegroundBrowserDocument()) return;
  if (isNativeApp() || browserAnalyticsOptedOut()) return;

  let config: Awaited<ReturnType<typeof api.analyticsConfig>>;
  try {
    config = await api.analyticsConfig();
  } catch {
    return;
  }
  if (!config?.enabled || config.memberOptedOut || browserAnalyticsOptedOut() || !isForegroundBrowserDocument()) return;

  const storage = getBrowserStorage();
  if (!storage) return;
  const now = Date.now();
  const browserKey = createBrowserKey(storage, now);
  if (!browserKey) return;
  const visit = createOrReuseVisit(storage, now);
  if (!visit || !persistVisitState(storage, visit)) return;

  const attemptId = beginVisitEvent(visit.visitKey, now);
  try {
    if (browserAnalyticsOptedOut() || !isForegroundBrowserDocument()) return;
    await api.analyticsEvent({
      browserKey,
      visitKey: visit.visitKey,
      kind: "page_view",
      feature: null,
      sourceCategory: visit.sourceCategory,
    });
    documentLandingSourceUsed = true;
    recordSuccessfulVisitEvent(storage, visit.visitKey);
  } catch (error) {
    const status = (error as { status?: number } | null)?.status;
    if (status === 403 || status === 409) clearBrowserIdentity(storage);
  } finally {
    endVisitEvent(visit.visitKey, attemptId);
  }
}

type NativeAnalyticsPreferences = Pick<typeof import("@capacitor/preferences").Preferences, "get" | "set" | "remove">;
let nativeAnalyticsIdentityQueue: Promise<void> = Promise.resolve();

async function withNativeAnalyticsIdentityLock<T>(operation: () => Promise<T>): Promise<T> {
  const previous = nativeAnalyticsIdentityQueue;
  let release!: () => void;
  nativeAnalyticsIdentityQueue = new Promise<void>((resolve) => { release = resolve; });
  await previous;
  try {
    return await operation();
  } finally {
    release();
  }
}

async function getNativeAnalyticsPreferences(): Promise<NativeAnalyticsPreferences | null> {
  try {
    const { Preferences } = await import("@capacitor/preferences");
    // A Capacitor proxy exposes `then`; returning it directly stalls async resolution.
    return { get: Preferences.get, set: Preferences.set, remove: Preferences.remove };
  } catch {
    return null;
  }
}

async function nativeAnalyticsOptedOut(preferences: NativeAnalyticsPreferences) {
  if (androidAnalyticsBlocked()) return true;
  try {
    const { value } = await preferences.get({ key: NATIVE_ANALYTICS_OPT_OUT_KEY });
    return value === "true";
  } catch {
    return true;
  }
}

async function createNativeBrowserKey(preferences: NativeAnalyticsPreferences, now: number): Promise<string | null> {
  try {
    const [storedKey, storedExpiry] = await Promise.all([
      preferences.get({ key: NATIVE_ANALYTICS_BROWSER_KEY }),
      preferences.get({ key: NATIVE_ANALYTICS_BROWSER_KEY_EXPIRES_AT }),
    ]);
    const expiresAt = Number(storedExpiry.value);
    if (storedKey.value && UUID_PATTERN.test(storedKey.value) && Number.isFinite(expiresAt) && expiresAt > now) {
      return storedKey.value;
    }

    const browserKey = createRandomUuid();
    if (!browserKey) return null;
    await Promise.all([
      preferences.set({ key: NATIVE_ANALYTICS_BROWSER_KEY, value: browserKey }),
      preferences.set({ key: NATIVE_ANALYTICS_BROWSER_KEY_EXPIRES_AT, value: String(now + BROWSER_KEY_TTL_MS) }),
      preferences.remove({ key: NATIVE_ANALYTICS_VISIT_KEY }),
    ]);
    return browserKey;
  } catch {
    return null;
  }
}

async function createOrReuseNativeVisit(preferences: NativeAnalyticsPreferences, now: number): Promise<VisitState | null> {
  try {
    const storedVisit = await preferences.get({ key: NATIVE_ANALYTICS_VISIT_KEY });
    const previous = readVisitState(storedVisit.value);
    const pending = previous ? pendingVisitEvents.get(previous.visitKey) : undefined;
    const recentSuccess = previous?.lastEventAt !== null && previous?.lastEventAt !== undefined &&
      now >= previous.lastEventAt && now - previous.lastEventAt < VISIT_TIMEOUT_MS;
    const recentAttempt = pending && now >= pending.lastAttemptAt && now - pending.lastAttemptAt < VISIT_TIMEOUT_MS;
    if (previous && (recentSuccess || recentAttempt)) return previous;

    const visitKey = createRandomUuid();
    if (!visitKey) return null;
    const visit: VisitState = {
      visitKey,
      lastEventAt: null,
      sourceCategory: "unknown",
    };
    await preferences.set({ key: NATIVE_ANALYTICS_VISIT_KEY, value: JSON.stringify(visit) });
    return visit;
  } catch {
    return null;
  }
}

async function recordNativeSuccessfulVisitEvent(preferences: NativeAnalyticsPreferences, visitKey: string) {
  try {
    const storedVisit = await preferences.get({ key: NATIVE_ANALYTICS_VISIT_KEY });
    const current = readVisitState(storedVisit.value);
    if (current?.visitKey === visitKey) {
      await preferences.set({
        key: NATIVE_ANALYTICS_VISIT_KEY,
        value: JSON.stringify({ ...current, lastEventAt: Date.now() }),
      });
    }
  } catch { /* local event time is only session bookkeeping */ }
}

async function clearNativeBrowserIdentity(preferences: NativeAnalyticsPreferences) {
  await Promise.all([
    preferences.remove({ key: NATIVE_ANALYTICS_BROWSER_KEY }),
    preferences.remove({ key: NATIVE_ANALYTICS_BROWSER_KEY_EXPIRES_AT }),
    preferences.remove({ key: NATIVE_ANALYTICS_VISIT_KEY }),
  ]).catch(() => {});
}

async function beginNativeVisitEvent(
  preferences: NativeAnalyticsPreferences,
  now: number,
): Promise<{ browserKey: string; visit: VisitState; attemptId: number } | null> {
  return withNativeAnalyticsIdentityLock(async () => {
    if (await nativeAnalyticsOptedOut(preferences)) return null;
    const browserKey = await createNativeBrowserKey(preferences, now);
    const visit = browserKey ? await createOrReuseNativeVisit(preferences, now) : null;
    if (!browserKey || !visit) return null;
    const attemptId = beginVisitEvent(visit.visitKey, now);
    return { browserKey, visit, attemptId };
  });
}

/** Send one coarse foreground feature view for an authenticated member navigation. */
export async function emitAnalyticsFeatureAction(feature: AnalyticsFeature) {
  if (!isForegroundBrowserDocument() || browserAnalyticsOptedOut()) return;

  const native = isNativeApp();
  const nativePreferences = native ? await getNativeAnalyticsPreferences() : null;
  if (native && (!nativePreferences || await nativeAnalyticsOptedOut(nativePreferences))) return;

  let config: Awaited<ReturnType<typeof api.analyticsConfig>>;
  try {
    config = await api.analyticsConfig();
  } catch {
    return;
  }
  if (!config?.enabled || config.memberOptedOut || browserAnalyticsOptedOut() || !isForegroundBrowserDocument()) return;

  const now = Date.now();
  let browserKey: string | null;
  let visit: VisitState | null;
  let attemptId: number | null = null;
  const storage = native ? null : getBrowserStorage();
  if (native) {
    if (!nativePreferences) return;
    const nativeAttempt = await beginNativeVisitEvent(nativePreferences, now);
    if (!nativeAttempt) return;
    browserKey = nativeAttempt.browserKey;
    visit = nativeAttempt.visit;
    attemptId = nativeAttempt.attemptId;
  } else {
    if (!storage) return;
    browserKey = createBrowserKey(storage, now);
    visit = browserKey ? createOrReuseVisit(storage, now) : null;
    if (visit && !persistVisitState(storage, visit)) return;
  }
  if (!browserKey || !visit) return;

  if (attemptId === null) attemptId = beginVisitEvent(visit.visitKey, now);
  try {
    if (browserAnalyticsOptedOut() || !isForegroundBrowserDocument() ||
      (nativePreferences && await nativeAnalyticsOptedOut(nativePreferences))) return;
    await api.analyticsEvent({
      browserKey,
      visitKey: visit.visitKey,
      kind: "feature_action",
      feature,
      sourceCategory: visit.sourceCategory,
    });
    documentLandingSourceUsed = true;
    if (nativePreferences) await recordNativeSuccessfulVisitEvent(nativePreferences, visit.visitKey);
    else if (storage) recordSuccessfulVisitEvent(storage, visit.visitKey);
  } catch (error) {
    const status = (error as { status?: number } | null)?.status;
    if (status === 403 || status === 409) {
      if (nativePreferences) {
        await withNativeAnalyticsIdentityLock(async () => clearNativeBrowserIdentity(nativePreferences));
      }
      if (storage) clearBrowserIdentity(storage);
    }
  } finally {
    endVisitEvent(visit.visitKey, attemptId);
  }
}

/** Object locally and, when there is an identity to find, remove server records. */
export async function objectToAnalytics() {
  const storage = getBrowserStorage();
  let browserKey: string | undefined;
  let nativeBrowserKey: string | undefined;
  if (storage) {
    try { storage.setItem(ANALYTICS_OPT_OUT_KEY, "true"); } catch { /* the server objection still applies to a signed-in member */ }
    try { browserKey = storage.getItem(ANALYTICS_BROWSER_KEY) ?? undefined; } catch { /* continue with member objection */ }
  }
  disableGoogleAnalytics();
  // Mirror only the choice; failure must not prevent the existing server cleanup.
  const safeSettingsWrite = persistAndroidAnalyticsOptOut().catch(() => {});
  const nativePreferences = isNativeApp() ? await getNativeAnalyticsPreferences() : null;
  if (nativePreferences) {
    try {
      await nativePreferences.set({ key: NATIVE_ANALYTICS_OPT_OUT_KEY, value: "true" });
      await withNativeAnalyticsIdentityLock(async () => {
        nativeBrowserKey = (await nativePreferences.get({ key: NATIVE_ANALYTICS_BROWSER_KEY })).value ?? undefined;
      });
    } catch { /* the server objection still applies to a signed-in member */ }
  }
  await safeSettingsWrite;
  if (!nativeBrowserKey && !browserKey && !getToken()) return false;

  if (storage) {
    try { storage.setItem(ANALYTICS_OBJECTION_PENDING_KEY, "true"); } catch { /* the local opt-out still prevents collection */ }
  }
  if (nativePreferences) {
    try { await nativePreferences.set({ key: NATIVE_ANALYTICS_OBJECTION_PENDING_KEY, value: "true" }); }
    catch { /* the browser marker still supports a retry when available */ }
  }

  await api.objectToAnalytics(nativeBrowserKey ?? browserKey);
  if (nativePreferences) {
    await withNativeAnalyticsIdentityLock(async () => clearNativeBrowserIdentity(nativePreferences));
    await nativePreferences.remove({ key: NATIVE_ANALYTICS_OBJECTION_PENDING_KEY }).catch(() => {});
  }
  if (storage) {
    clearBrowserIdentity(storage);
    try { storage.removeItem(ANALYTICS_OBJECTION_PENDING_KEY); } catch { /* the objection succeeded on the server */ }
  }
  return true;
}
